import type { FrameDataFragment, ImageParams } from 'generated/graphql';
import type { FullSingleLayerStack } from '../../../ViewportsConfigurations/types';
import type { $AxisDirection } from '@kitware/vtk.js';

import { calculateAxis } from '../../utils/math';
import { BaseImagingProvider, emitterEvents } from './BaseImagingProvider';
import type { BaseImagingProviderArgs } from './BaseImagingProvider';
import { DRE_ALL_2D_VIEWPORT_TYPES, REVIEWED_THRESHOLD } from 'config/constants';
import type { DreAllViewportTypes, RendererKey } from 'config/constants';
import { createColorTransferFunctionFromLUTSequence } from '../../utils/createLutFromDicom';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import { invertColorTransferFunction } from '../../utils/invertColorTransferFunction';

// estimateMemory() is the nominal pixel volume; useViewerMemoryConfig applies render overheads.

export class BaseSingleStackImagingProvider<
  T extends FullSingleLayerStack = FullSingleLayerStack,
> extends BaseImagingProvider<T> {
  #frameAxes: Set<$AxisDirection>;
  #validDirection: boolean;
  #frameSmidsMap: {
    [key: number]: string;
  };
  #viewedFrameIndexes: Map<string, Map<DreAllViewportTypes, Set<number>>>;
  #containsFramesFromMultipleSeries: boolean | undefined;
  #containsFramesFromMultipleSOPClasses: boolean = false;
  colorTransferFunctionsSmidMap: Map<string, vtkColorTransferFunction | null> = new Map();
  #sopClassUIDMap: Set<string> = new Set();
  /**
   * One-way latch: once a volume allocation fails we permanently treat this stack as non-3Dable.
   * Intentionally not a tri-state — there is no path that re-enables 3D after a failed allocation.
   */
  #forcedNon3Dable: boolean = false;

  constructor(providerArgs: BaseImagingProviderArgs<T>) {
    super(providerArgs);

    // Calculate these once for the getters to reference
    this.#frameAxes = new Set(
      this.stack.frames.map((frame) => {
        return calculateAxis(frame.direction.data);
      })
    );

    this.#validDirection = this.stack.frames.every((frame) => frame.direction.validDirection);
    this.#frameSmidsMap = Object.fromEntries(
      this.stack.frames.map((frame, index) => [index, frame.smid])
    );

    const seriesSmids = new Set(this.stack.frames.map((frame) => frame.series.smid));
    this.#viewedFrameIndexes = new Map();
    seriesSmids.forEach((viewType) => {
      const seriesForView = new Map();
      DRE_ALL_2D_VIEWPORT_TYPES.forEach((seriesSmid) => seriesForView.set(seriesSmid, new Set()));
      this.#viewedFrameIndexes.set(viewType, seriesForView);
    });
    this.stack.frames.forEach((frame) => {
      const voiLutSequence = frame?.modules?.voiLut?.voiLutSequence;
      if (voiLutSequence?.[0] != null) {
        const colorTransferFunction = createColorTransferFunctionFromLUTSequence(voiLutSequence[0]);
        this.colorTransferFunctionsSmidMap.set(frame.smid, colorTransferFunction);
        this.colorTransferFunctionsSmidMap.set(
          `${frame.smid}-inverted`,
          invertColorTransferFunction(colorTransferFunction)
        );
      }

      if (frame.sopClassUID != null) {
        this.#sopClassUIDMap.add(frame.sopClassUID);
      }
    });
    this.#containsFramesFromMultipleSeries = seriesSmids.size > 1;
    this.#containsFramesFromMultipleSOPClasses = this.#sopClassUIDMap.size > 1;
  }

  hasValidDirection(): boolean {
    return this.#validDirection;
  }

  /* Map of the frame order in the series to the frame SMID */
  get frameSmidsMap(): {
    [key: number]: string;
  } {
    return this.#frameSmidsMap;
  }
  /* Number of slices / frames in the series */
  get stackSize(): number {
    return this.stack.frames.length;
  }

  /* DICOM instance tags for this stack */
  get frameTags(): ReadonlyArray<FrameDataFragment> {
    return this.stack.frames;
  }

  /**
   * General parameters for the image used in calculating transforms, positioning, etc.
   */
  get imageParams(): ImageParams {
    return this.stack.imageParams;
  }

  getFrameAxes(): Set<$AxisDirection> {
    return this.#frameAxes;
  }

  hasPixels(): boolean {
    return this.stack.frames[0].hasPixels;
  }

  canRender(renderEngine: string): boolean {
    const engine = this.stack.supportedRenderers[renderEngine as RendererKey];
    return engine ? true : false;
  }

  /**
   * Force this stack to be treated as non-3Dable for its remaining lifetime.
   * Used when volume typed array allocation fails and we need to fall back to per-frame loading.
   */
  overrideIs3DableToFalse(): void {
    if (this.#forcedNon3Dable) return;
    this.#forcedNon3Dable = true;
    // Notify the React layer so a viewport hung in a 3D/MPR view type can coerce back to 2D before
    // it tries to render against the now-released volume. Idempotent: only the first downgrade emits.
    this.emitter.emit(emitterEvents.is3DableDowngraded);
  }

  is3Dable(): boolean {
    return this.#forcedNon3Dable ? false : this.stack.is3Dable;
  }

  estimateMemory(): number {
    const perChannelBytes = this.supportedTextures.norm16 ? 2 : 4;
    return this.stack.frames.reduce(
      (total, frame) =>
        total + (frame.colorChannels ?? 1) * perChannelBytes * frame.size[0] * frame.size[1],
      0
    );
  }

  estimatePerFrameMemory(): number {
    const frameSize = this.stack.frames.length;
    return this.estimateMemory() / frameSize;
  }

  seriesAreHomogenous(): boolean {
    return !this.#containsFramesFromMultipleSeries;
  }

  isStackMultiSOPClass(): boolean {
    return this.#containsFramesFromMultipleSOPClasses;
  }

  getNumberOfColorChannels(frameIndex: number): number {
    const frame = this.stack.frames[frameIndex];
    const firstFrame = this.stack.frames[0];
    return frame?.colorChannels ?? firstFrame?.colorChannels ?? 1;
  }

  dispose(): void {
    this.colorTransferFunctionsSmidMap.clear();
    super.dispose();
  }

  markSliceViewed(viewType: DreAllViewportTypes, slice: number): Set<string> {
    const seriesSmid = this.getFrameTagsForViewIndex(viewType, slice)?.series.smid ?? '';
    const seriesViewTypeViewedSlicesSet = this.#viewedFrameIndexes.get(seriesSmid)?.get(viewType);
    if (seriesViewTypeViewedSlicesSet == null) return new Set();

    seriesViewTypeViewedSlicesSet.add(slice);

    const reviewedSeries = new Set<string>();
    if (this.is3Dable()) {
      const [minSlices, maxSlices] = this._getViewTypeBounds(viewType);
      const viewTypeSize = Math.abs(maxSlices - minSlices);
      if (seriesViewTypeViewedSlicesSet.size >= viewTypeSize * REVIEWED_THRESHOLD) {
        reviewedSeries.add(seriesSmid);
      }
    } else {
      const framesMatchingSeriesCount = this.frameTags.reduce(
        (smfc, frame) => (smfc + frame.series.smid === seriesSmid ? 1 : 0),
        0
      );

      if (seriesViewTypeViewedSlicesSet.size >= framesMatchingSeriesCount * REVIEWED_THRESHOLD) {
        reviewedSeries.add(seriesSmid);
      }
    }

    return reviewedSeries;
  }
}
